import {useMutation} from "@apollo/client";
import classNames from "classnames";
import isEmpty from "lodash/isEmpty";
import {ReactNode, useContext, useEffect, useMemo, useState} from "react";
import {FormProvider, useForm} from "react-hook-form";

import Button from "@/components/button/Button.tsx";
import {Divider} from "@/components/divider/Divider.tsx";
import {Dot} from "@/components/dot/Dot.tsx";
import FormErrorAlerts from "@/components/form/FormErrorAlerts.tsx";
import {TabContext} from "@/lib/hooks/useTabs.tsx";
import {useZendeskWidgetOffset} from "@/lib/integrations/useZendeskWidgetOffset.ts";
import {GQL_STATUS_PAGE_QUERY, GQL_UPDATE_STATUS_PAGE_V2, UpdateStatusPageV2Result} from "@/lib/queries/statuspages.ts";
import {ResponseStatus, ServerErrors} from "@/lib/types/common.ts";
import {buildPreviewOverrides} from "@/pages/statuspages/shared/livePreview.ts";
import {SharedStatusPageFormData, SharedStatusPageFormKeys} from "@/pages/statuspages/shared/StatusPage.types.ts";
import {statusPageFormDataProcessing} from "@/pages/statuspages/shared/StatusPageForm.utils.ts";
import {
  StatusPageContext,
  StatusPagePreviewActionsContext,
} from "@/pages/statuspages/statuspages-manage/ManageStatusPageTabContainer.utils.tsx";
import {
  generateStatusPageDefaultValues,
  UpdateStatusPageYupResolver,
} from "@/pages/statuspages/statuspages-manage/ManageStatusPageUpdateFormTab.utils.ts";
import {setServerErrors} from "@/pages/vault/vaultForm/CredentialFormUtils.ts";

import styles from "./ManageStatusPageUpdateFormTab.module.scss";

export const ManageStatusPageUpdateFormTab = ({
  children,
  fields,
}: {
  children: ReactNode;
  fields: SharedStatusPageFormKeys[];
}) => {
  const {setTabBlocker, setOnBypass} = useContext(TabContext);
  const {statusPage} = useContext(StatusPageContext);
  // Preview actions live in a separate, stable context so pushing overrides here does not re-render
  // this wrapper or the active tab subtree (only the preview surfaces consume the preview state).
  const {reloadPreview, setPreviewOverrides} = useContext(StatusPagePreviewActionsContext);
  useZendeskWidgetOffset("80px");
  const [updateStatusPage, {data}] = useMutation<UpdateStatusPageV2Result>(GQL_UPDATE_STATUS_PAGE_V2, {
    refetchQueries: [GQL_STATUS_PAGE_QUERY],
    awaitRefetchQueries: true,
  });

  const defaultValues = useMemo(() => generateStatusPageDefaultValues(statusPage), [statusPage]);

  const methods = useForm<SharedStatusPageFormData>({
    resolver: UpdateStatusPageYupResolver,
    reValidateMode: "onChange",
    mode: "onChange",
    defaultValues: {
      ...defaultValues,
    },
  });
  const {formState} = methods;

  const newStatusPageErrors =
    data?.response?.status === ResponseStatus.ERROR ? (data?.response?.message as ServerErrors) : undefined;

  const [serverErrorsState, setServerErrorsState] = useState<ServerErrors | undefined>(newStatusPageErrors);
  const [errorsNotInForm, setErrorsNotInForm] = useState<string[] | undefined>(undefined);

  const isBlocked = formState.isDirty && !formState.isSubmitSuccessful;

  // Push debounced form values to the live preview iframe (when it is open). A `watch` subscription
  // is used instead of `useWatch` so keystrokes do not re-render this wrapper or its tab subtree;
  // the resulting overrides flow into a separate preview-state context that only the preview
  // surfaces consume, so this component stays isolated from preview updates.
  useEffect(() => {
    let handle: ReturnType<typeof setTimeout> | undefined;
    const subscription = methods.watch((values, {name}) => {
      // Only react to genuine field edits. Bulk updates such as a post-save `reset` fire with no
      // `name`; ignoring them keeps overrides cleared after save until the user edits again.
      if (name === undefined) return;
      if (handle) clearTimeout(handle);
      handle = setTimeout(() => {
        setPreviewOverrides(buildPreviewOverrides(values));
      }, 200);
    });
    return () => {
      if (handle) clearTimeout(handle);
      subscription.unsubscribe();
    };
  }, [methods, setPreviewOverrides]);

  useEffect(() => {
    setOnBypass(() => {
      methods.reset();
    });
    return () => setOnBypass(undefined);
  }, [methods, setOnBypass]);

  useEffect(() => {
    setTabBlocker(isBlocked);
  }, [isBlocked, setTabBlocker]);

  useEffect(() => {
    if (formState.isSubmitSuccessful) {
      methods.reset(defaultValues);
    }
  }, [defaultValues, formState.isSubmitSuccessful, methods]);

  useEffect(() => {
    if (serverErrorsState) {
      const errorsNotInForm = setServerErrors(serverErrorsState, methods.setError, "", fields);
      setErrorsNotInForm(errorsNotInForm);
    }
  }, [serverErrorsState, methods.setError, fields]);

  const onSubmit = async (formData: SharedStatusPageFormData) => {
    const response = await updateStatusPage({
      variables: {
        id: statusPage.id,
        data: statusPageFormDataProcessing(formData),
      },
    });

    if (response?.data?.response?.status === ResponseStatus.SUCCESS && response.data.response.data.id) {
      setServerErrorsState(undefined);
      methods.reset(formData);
      // The reloaded iframe re-fetches the saved, server-normalized page. Clear the form-derived
      // overrides so stale client-only-sanitized values aren't re-posted over the saved state; the
      // next edit pushes fresh overrides again.
      setPreviewOverrides(null);
      reloadPreview();
      return;
    } else {
      const errors =
        response?.data?.response?.status === ResponseStatus.ERROR
          ? (response?.data?.response?.message as ServerErrors)
          : undefined;
      setServerErrorsState(errors);
    }
  };

  const formErrors = [
    ...(errorsNotInForm && !isEmpty(errorsNotInForm) ? ["Something went wrong, please contact support"] : []),
    ...(serverErrorsState?.__all__ || []),
  ];

  return (
    <FormProvider {...methods}>
      {formErrors.length > 0 && <FormErrorAlerts errors={formErrors} className="mb-4 px-3" />}
      <form
        onSubmit={(e) => {
          e.preventDefault();
          methods.handleSubmit(onSubmit)(e);
        }}
        id={"status-page-update-form"}
      >
        <div className={"d-flex flex-column w-100 h-100"}>
          <div className={"flex-grow-1 px-4"}>{children}</div>
          <div
            className={classNames(
              "position-sticky bottom-0 py-3 d-flex flex-column gap-3 pulse-bg-white",
              styles.saveFooter
            )}
          >
            <Divider />
            <div className={"d-flex gap-2 px-3 ms-auto"}>
              {formState.isDirty && (
                <div className={"d-flex gap-2 align-items-center"}>
                  <Dot variant={"orange"} />
                  <span className={"pulse-color-orange-600 pulseTextBodySmall"}>Unsaved</span>
                </div>
              )}
              <Button variant={"secondary"} disabled={!formState.isDirty} onClick={() => methods.reset()}>
                Discard
              </Button>
              <Button className={"align-self-baseline"} type="submit" disabled={!formState.isDirty}>
                Save Changes
              </Button>
            </div>
          </div>
        </div>
      </form>
    </FormProvider>
  );
};
